/**
 * シフト必要人数（staffing_targets）関連のユーティリティ関数。
 * テスト可能にするため Shift.vue から切り出し。
 */

/**
 * 指定日の必要人数を解決する。日別上書き > 曜日デフォルト の優先順。
 *
 * @param {Array<{dayOfWeek: number|null, date: string|null, requiredCount: number}>} targets
 * @param {string} dateStr yyyy-MM-dd
 * @returns {number|null} 必要人数（未設定なら null）
 */
export const resolveRequiredCount = (targets, dateStr) => {
  const list = targets || []
  const override = list.find(t => t.date === dateStr)
  if (override) return override.requiredCount

  const wday = new Date(dateStr).getDay()
  const weekdayDefault = list.find(t => t.date == null && t.dayOfWeek === wday)

  return weekdayDefault ? weekdayDefault.requiredCount : null
}

/**
 * work_schedules（serializer の data 要素）から approved の件数を日付ごとに集計する。
 *
 * @param {Array<object>} schedules
 * @returns {Object<string, number>} { 'yyyy-MM-dd': 件数 }
 */
export const countApprovedByDate = schedules => (schedules || []).reduce((acc, s) => {
  const attrs = s.attributes || s
  if (attrs.status !== 'approved' || !attrs.date) return acc
  const dateStr = String(attrs.date).slice(0, 10)
  acc[dateStr] = (acc[dateStr] || 0) + 1

  return acc
}, {})

/**
 * 日別の充足サマリを作る。必要人数が設定されている日のみ返す。
 *
 * @param {Array<object>} targets staffing_targets（camelCase 属性）
 * @param {Object<string, number>} approvedByDate 日付ごとの承認済み人数
 * @param {Array<string>} dateStrs 対象日（yyyy-MM-dd）
 * @returns {Array<{date: string, required: number, approved: number, isShort: boolean}>}
 */
export const buildStaffingSummary = (targets, approvedByDate, dateStrs) => (dateStrs || [])
  .map(dateStr => {
    const required = resolveRequiredCount(targets, dateStr)
    if (required == null) return null
    const approved = approvedByDate[dateStr] || 0

    return {
      date: dateStr,
      required,
      approved,
      isShort: approved < required,
    }
  })
  .filter(row => row !== null)

/**
 * 希望一覧を日付ごとにグループ化する。日付昇順、
 * グループ内は時刻指定あり（早い順）→ 時間未定 の順。
 *
 * @param {Array<{date: string, startAt: string|null}>} requests
 * @returns {Array<{date: string, requests: Array<object>}>}
 */
export const groupRequestsByDate = requests => {
  const map = {};

  (requests || []).forEach(request => {
    const dateStr = String(request.date).slice(0, 10)
    if (!map[dateStr]) map[dateStr] = []
    map[dateStr].push(request)
  })

  return Object.keys(map)
    .sort()
    .map(date => ({
      date,
      requests: map[date].slice().sort((a, b) => {
        if (!a.startAt && !b.startAt) return 0
        if (!a.startAt) return 1
        if (!b.startAt) return -1

        return a.startAt < b.startAt ? -1 : 1
      }),
    }))
}

/**
 * work_schedules（serializer の data 要素）から approved のキャスト名を日付ごとに集める。
 *
 * @param {Array<object>} schedules
 * @returns {Object<string, Array<string>>} { 'yyyy-MM-dd': ['名前', ...] }
 */
export const collectApprovedNamesByDate = schedules => (schedules || []).reduce((acc, s) => {
  const attrs = s.attributes || s
  if (attrs.status !== 'approved' || !attrs.date) return acc
  const dateStr = String(attrs.date).slice(0, 10)
  if (!acc[dateStr]) acc[dateStr] = []
  acc[dateStr].push(attrs.userName)

  return acc
}, {})

/**
 * 勤怠ステータス付き（遅刻・欠勤など）のタイムカードを営業日別に集める。
 * 営業日は waiter timecards API の serializer meta.date（translate_time 済み）を使う。
 *
 * @param {Array<object>} timecards serializer の data 要素
 * @returns {Object<string, Array<{userName: string, statusName: string}>>}
 */
export const collectTimecardStatusesByDate = timecards => (timecards || []).reduce((acc, tc) => {
  const statusName = tc?.meta?.timecardStatusName
  const dateStr = tc?.meta?.date
  if (!statusName || !dateStr) return acc
  if (!acc[dateStr]) acc[dateStr] = []
  acc[dateStr].push({ userName: tc.meta.userName, statusName })

  return acc
}, {})

/**
 * 予約を reservedDate 別に集める（キャンセル済み＝canceledAt ありは除外）。
 *
 * @param {Array<object>} reservations serializer の data 要素
 * @returns {Object<string, Array<object>>} { 'yyyy-MM-dd': [{id, startTime, customerCount, ...}] }
 */
export const groupReservationsByDate = reservations => (reservations || []).reduce((acc, r) => {
  const attrs = r.attributes || r
  if (attrs.canceledAt || !attrs.reservedDate) return acc
  const dateStr = String(attrs.reservedDate).slice(0, 10)
  if (!acc[dateStr]) acc[dateStr] = []
  acc[dateStr].push({ id: r.id, ...attrs })

  return acc
}, {})

/**
 * シフトの日付と時刻を「yyyy-MM-dd HH:mm」に組み立てる。
 * 深夜営業のため、基準時刻（出勤）以前の時刻は翌日扱いにする。
 *
 * @param {string} dateStr 営業日（yyyy-MM-dd）
 * @param {string} timeStr HH:mm
 * @param {string|null} baseTimeStr 基準時刻（出勤 HH:mm）。省略時は当日のまま
 * @returns {string}
 */
export const buildShiftDateTime = (dateStr, timeStr, baseTimeStr = null) => {
  const date = new Date(dateStr)
  if (baseTimeStr && timeStr <= baseTimeStr) date.setDate(date.getDate() + 1)

  const pad = n => String(n).padStart(2, '0')

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${timeStr}`
}

/**
 * 月サマリー（不足日数・延べ出勤予定・予約合計）を集計する。
 *
 * @param {Array<{isShort: boolean}>} staffingRows buildStaffingSummary の結果
 * @param {Object<string, number>} approvedByDate
 * @param {Object<string, Array>} reservationsByDate
 * @param {Array<string>} dateStrs 対象月の日付一覧
 * @returns {{shortDays: number, totalScheduled: number, totalReservations: number}}
 */
export const buildMonthSummary = (staffingRows, approvedByDate, reservationsByDate, dateStrs) => ({
  shortDays: (staffingRows || []).filter(row => row.isShort).length,
  totalScheduled: (dateStrs || []).reduce((sum, d) => sum + (approvedByDate[d] || 0), 0),
  totalReservations: (dateStrs || []).reduce((sum, d) => sum + ((reservationsByDate[d] || []).length), 0),
})
